import { notification } from "antd";
import { push } from "connected-react-router";
import { endOfDay } from "date-fns";
import { defineMessages } from "react-intl";
import { all, call, fork, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import invariant from "tiny-invariant";
import { Action, isActionOf } from "typesafe-actions";
import { v4 } from "uuid";

import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel, {
  Condition,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import type PlatformListRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformListRequest";

import { openChartMention } from "@app/component/EntityMentions/store/actions";
import i18nService from "@app/config/I18nService";
import TimelineAction from "@app/enum/TimelineAction";
import TimelineEventType from "@app/scene/dashboard/enum/TimelineEventType";
import { getEventTypesByViewFilter } from "@app/scene/dashboard/enum/TimelineViewFilter";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { EVENTS_PAGE_SIZE } from "@app/scene/dashboard/store/const";
import EventFilters from "@app/scene/dashboard/store/EventFilters";
import getOrConditionByEventTypes from "@app/scene/dashboard/store/getOrConditionByEventTypes";
import { getBoards, getEventsFilters, getEventsTotal } from "@app/scene/dashboard/store/selectors";
import fillTimelineEventsPinColor from "@app/scene/dashboard/store/utils/fillTimelineEventsPinColor";
import getResultingScope from "@app/scene/dashboard/store/utils/getResultingScope";
import dashboardSettings from "@app/scene/dashboard/utils/dashboardSettings";
import getChartPathname from "@app/scene/dashboard/utils/getChartPathname";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUser, getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { isMember, isReadOnly } from "@app/store/iam/util";
import Dashboard from "@app/types/dashboard/Dashboard";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";
import TimelineEvent, { RawTimelineEvent } from "@app/types/events/TimelineEvent";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";
import normalizeRawTimelineEvents from "@app/util/timeline/normalizeRawTimelineEvents";

import {
  addCard,
  changeScope,
  fetchTimelineEvents,
  overrideTeamDashboards,
  removeCard,
  saveCard,
  saveDashboardLayout,
  saveDashboardSettings,
} from "./actions";
import { activitiesLoggedSagas } from "./cards/activitiesLogged/sagas";
import { activitiesOverdueSagas } from "./cards/activitiesOverdue/sagas";
import { averageStopsPerRouteSagas } from "./cards/averageStopsPerRoute/sagas";
import { checkInsSagas } from "./cards/checkIns/sagas";
import { closedStageDealAmountSagas } from "./cards/closedStageDealAmount/sagas";
import { closedStageDealCountSagas } from "./cards/closedStageDealCount/sagas";
import { customerFaceTimeSagas } from "./cards/customerFaceTime/sagas";
import { distanceTravelledSagas } from "./cards/distanceTravelled/sagas";
import { efficiencyTrackerSagas } from "./cards/efficiencyTracker/sagas";
import { fieldTimeSagas } from "./cards/fieldTime/sagas";
import { mileageTrackingSagas } from "./cards/mileageTracking/sagas";
import { newRecordsSagas } from "./cards/newRecords/sagas";
import { newRoutesSagas } from "./cards/newRoutes/sagas";
import { noContactInSagas } from "./cards/noContactIn/sagas";
import { noteCompletionSagas } from "./cards/noteCompletion/sagas";
import { openStageDealAmountSagas } from "./cards/openStageDealAmount/sagas";
import { outOfFrequencySagas } from "./cards/outOfFrequency/sagas";
import { recordsCadenceEffectivenessSagas } from "./cards/recordsCadenceEffectiveness/sagas";
import { recordsPastDueSagas } from "./cards/recordsPastDue/sagas";
import { skippedRecordsSagas } from "./cards/skippedRecords/sagas";
import { upcomingActivitiesSagas } from "./cards/upcomingActivities/sagas";
import { coachingSagas } from "./coaching/sagas";
import { executiveSummaryScheduleSagas } from "./executiveSummarySchedule/sagas";
import { selectionEntitiesSagas } from "./selectionEntities/sagas";
import { getScope } from "./selectors";
import { stackRankSagas } from "./stackRank/sagas";
import { weeklySummarySagas } from "./weeklySummary/sagas";
import { weeklySummarySettingsSagas } from "./weeklySummarySettings/sagas";
import { wordCloudSagas } from "./wordCloud/sagas";
import { wordCloudSettingsSagas } from "./wordCloudSettings/sagas";

const VALID_TIMELINE_ACTIONS: ReadonlySet<TimelineAction> = new Set([
  TimelineAction.ACTIVITY_ASSIGNED,
  TimelineAction.ACTIVITY_COMPLETED,
  TimelineAction.FILE_ADDED,
  TimelineAction.MENTION,
  TimelineAction.NOTE_ADDED,
  TimelineAction.RECORD_ADDED,
  TimelineAction.RECORD_UPDATED,
  TimelineAction.ROUTE_CREATED,
]);

function* fetchAndProcessTimelineEvents(
  orgId: Organization["id"],
  request: Partial<PlatformListRequest>
) {
  const response: ListResponse<RawTimelineEvent> = yield callApi(
    "fetchTimelineEvents",
    orgId,
    request
  );

  const data = normalizeRawTimelineEvents(response.data, VALID_TIMELINE_ACTIONS);

  return { ...response, data } as ListResponse<TimelineEvent>;
}

const notificationMessages = defineMessages({
  description: {
    id: "dashboard.overrideTeamDashboard.success.notification.description",
    defaultMessage:
      "All {allTeamsUsers, select, true {team and user} other {selected team}} dashboards now have the same structure as the organization’s dashboard",
    description: "Team dashboards override success notification description",
  },
  title: {
    id: "dashboard.overrideTeamDashboard.success.notification.title",
    defaultMessage: "Org dashboards applied successfully",
    description: "Team dashboards override success notification title",
  },
});

function* onChangeScope({ payload }: ReturnType<typeof changeScope.request>) {
  try {
    const { scope, view } = payload;
    const orgId: Organization["id"] = yield select(getOrganizationId);

    if (view === ViewType.DASHBOARD) {
      const isTeamUserScope = !!scope.teamId && !!scope.userId;
      const requests = [callApi("fetchDashboards", orgId, scope)];
      if (isTeamUserScope) {
        // also fetch team board, which is used as a root board for team members
        requests.push(callApi("fetchDashboards", orgId, { ...scope, userId: undefined }));
      }
      const responses: ListResponse<Dashboard>[] = yield all(requests);
      yield put(changeScope.success({ boards: responses.flatMap(({ data }) => data), view }));
    } else if (view === ViewType.TIMELINE) {
      let oldestEventDate: Date | undefined = undefined;
      const $filters: PlatformFilterModel | undefined = yield getTimelineFilters(
        payload.scope,
        false,
        true
      );
      if ($filters) {
        const events: ListResponse<TimelineEvent> = yield call(
          fetchAndProcessTimelineEvents,
          orgId,
          {
            $filters,
            // technically, we only need 1 event, but platform filters out events which are related to deleted
            // entities, so it's not guaranteed to return any results if we use $limit=1. For more info:
            // https://mapmycustomers.slack.com/archives/C05NSL22M0T/p1717154920444389?thread_ts=1717152649.150669&cid=C05NSL22M0T
            $limit: 100,
            $order: "updatedAt",
          }
        );
        if (events.data.length) {
          const date = parseApiDate(events.data[0].updatedAt);
          if (isValidDate(date)) {
            oldestEventDate = date;
          }
        }
      }
      yield put(changeScope.success({ oldestEventDate, view }));
    } else if (view === ViewType.COACHING) {
      yield put(changeScope.success({ view }));
    } else if (view === ViewType.EXECUTIVE_SUMMARY) {
      yield put(changeScope.success({ view }));
    } else if (view === ViewType.TASKS) {
      yield put(changeScope.success({ view }));
    } else {
      invariant(false, `Unexpected view type: ${view}`);
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(changeScope.failure());
  }
}

const isLoadMorePayload = (payload: ReturnType<typeof fetchTimelineEvents.request>["payload"]) =>
  !(payload.eventTypes || "query" in payload || "startDate" in payload || "viewFilter" in payload);

function* getTimelineFilters(scope: Scope, isLoadMoreRequest: boolean, ignoreStartDate?: boolean) {
  let $and: PlatformFilterCondition[] = [];
  if (scope.userId) {
    $and.push({ "updatedBy.id": { $in: [scope.userId] } });
  } else if (scope.teamId) {
    $and.push({ teamId: { $in: [scope.teamId] } });
  }

  const { eventTypes, offset, query, startDate, viewFilter }: EventFilters =
    yield select(getEventsFilters);
  const eventsTotal: number = yield select(getEventsTotal);

  if (startDate && !ignoreStartDate) {
    $and.push({ updatedAt: { $lte: endOfDay(startDate).toISOString() } });
  }

  if (query) {
    $and.push({ "entity.name": { $in: query } });
  }

  const viewFilterEventTypes = getEventTypesByViewFilter(viewFilter);
  // event types take precedence
  const finalEventTypes = viewFilterEventTypes.filter((type) => eventTypes.includes(type));

  let $or = getOrConditionByEventTypes(finalEventTypes);
  if (!$or.length || (isLoadMoreRequest && offset > eventsTotal)) {
    // nothing to fetch
    return undefined;
  }

  if (scope.userId && finalEventTypes.includes(TimelineEventType.MENTIONS)) {
    // we need a special handling, more info:
    // https://mapmycustomers.atlassian.net/browse/TART-17523?focusedCommentId=46849

    // We know that the item with an "action" key is present there due to getOrConditionByEventTypes
    // @ts-ignore
    const actions: Condition = $or.find((item) => item.action)!.action;
    // removing mentions from there, since we'll add them separately
    actions.$in = (actions.$in as TimelineAction[]).filter(
      (action) => action !== TimelineAction.MENTION
    );

    // We know that the item with an "updatedBy.id" key is present there
    // @ts-ignore
    const andForMentions = $and.filter((item) => !item["updatedBy.id"]);
    andForMentions.push({ "updatedBy.id": { $nin: [scope.userId] } });
    andForMentions.push({ action: { $in: [TimelineAction.MENTION] } });

    const $finalOr: PlatformFilterCondition[] = [];
    if ((actions.$in as TimelineAction[]).length) {
      $and.push({ $or });
      $finalOr.push({ $and });
    }
    $finalOr.push({ $and: andForMentions });

    $and = [];
    $or = $finalOr;
  }

  return { $and, $or };
}

function* onFetchTimelineEvents({ payload }: ReturnType<typeof fetchTimelineEvents.request>) {
  try {
    const isLoadMoreRequest = isLoadMorePayload(payload);

    if (payload.eventTypes) {
      yield call([dashboardSettings, dashboardSettings.setVisibleEventTypes], payload.eventTypes);
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { scope } = payload;

    const $filters: PlatformFilterModel | undefined = yield getTimelineFilters(
      scope,
      isLoadMoreRequest
    );
    if (!$filters) {
      // nothing to fetch
      yield put(
        fetchTimelineEvents.success({
          append: isLoadMoreRequest,
          events: [],
          hasMore: false,
          total: !isLoadMoreRequest ? 0 : undefined,
        })
      );
      if (payload.onComplete) {
        yield call(payload.onComplete);
      }
      return;
    }

    const { offset }: EventFilters = yield select(getEventsFilters);

    const events: ListResponse<TimelineEvent> = yield call(fetchAndProcessTimelineEvents, orgId, {
      $filters,
      $limit: EVENTS_PAGE_SIZE,
      $offset: offset,
      $order: "-updatedAt",
    });

    // Resolved before the page is dispatched, so a card never renders without its color.
    yield call(fillTimelineEventsPinColor, orgId, events.data);

    yield put(
      fetchTimelineEvents.success({
        append: isLoadMoreRequest,
        events: events.data,
        // A non-empty page means there may be more to fetch; an empty page marks the end.
        // The platform over-reports `total` (it filters out events of deleted entities), so
        // page emptiness — not `events.length < total` — is the reliable stop signal.
        hasMore: events.data.length > 0,
        total: events.total,
      })
    );
    if (payload.onComplete) {
      yield call(payload.onComplete);
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(fetchTimelineEvents.failure(error));
  }
}

// TODO: consider combining all onAddCard, onSaveCard, onRemove card into one function because they're all the same
function* onAddCard({ payload }: ReturnType<typeof addCard.request>) {
  try {
    const scope = getResultingScope(payload.scope);
    const boards: Dashboard[] = yield select(getBoards);

    // this board is already updated in redux store and contains the new card
    const board = boards.find(
      (board) => board.teamId === scope.teamId && board.userId === scope.userId
    );

    if (!board) {
      yield put(addCard.failure());
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedBoard: Dashboard = yield callApi("updateDashboard", orgId, board);

    yield put(addCard.success(updatedBoard));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(addCard.failure());
  }
}

function* onSaveCard({ payload }: ReturnType<typeof saveCard.request>) {
  try {
    const { scope } = payload;
    const isTeamUserScope = !!scope.teamId && !!scope.userId;
    const boards: Dashboard[] = yield select(getBoards);

    const currentUser: User = yield select(getCurrentUser);

    // these boards are already updated in redux store and has the card removed
    // Note: not all these boards are necessarily affected, e.g. when team member board
    // can remain unchanged. But we still save all of them.
    const affectedBoards = boards.filter(
      (board) =>
        (board.teamId === scope.teamId && board.userId === scope.userId) ||
        (isTeamUserScope &&
          !isMember(currentUser) &&
          !isReadOnly(currentUser) &&
          board.teamId === scope.teamId &&
          !board.userId)
    );

    if (!affectedBoards.length) {
      yield put(saveCard.failure());
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedBoards: Dashboard[] = yield all(
      affectedBoards.map((board) => callApi("updateDashboard", orgId, board))
    );

    yield put(saveCard.success(updatedBoards));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(saveCard.failure());
  }
}

function* onRemoveCard({ payload }: ReturnType<typeof removeCard.request>) {
  try {
    const { scope } = payload;
    const isTeamUserScope = !!scope.teamId && !!scope.userId;
    const boards: Dashboard[] = yield select(getBoards);

    // these boards are already updated in redux store and has the card removed
    // Note: not all these boards are necessarily affected, e.g. when team member board
    // can remain unchanged. But we still save all of them.
    const affectedBoards = boards.filter(
      (board) =>
        (board.teamId === scope.teamId && board.userId === scope.userId) ||
        (isTeamUserScope && board.teamId === scope.teamId && !board.userId)
    );

    if (!affectedBoards.length) {
      yield put(removeCard.failure());
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedBoards: Dashboard[] = yield all(
      affectedBoards.map((board) => callApi("updateDashboard", orgId, board))
    );

    yield put(removeCard.success(updatedBoards));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(removeCard.failure());
  }
}

function* onSaveDashboardLayout({ payload }: ReturnType<typeof saveDashboardLayout.request>) {
  try {
    const { cardPositions } = payload;
    const scope = getResultingScope(payload.scope);
    const boards: Dashboard[] = yield select(getBoards);

    const board = boards.find(
      (board) => board.teamId === scope.teamId && board.userId === scope.userId
    );

    if (!board) {
      yield put(saveDashboardLayout.failure());
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const boardPayload: Dashboard = {
      ...board,
      settings: {
        ...board.settings,
        cards: board.settings.cards.map((card: DashboardCard) => ({
          ...card,
          ...(cardPositions[String(card.id)] ?? {}),
        })),
      },
    };

    const updatedBoard: Dashboard = yield callApi("updateDashboard", orgId, boardPayload);

    yield put(saveDashboardLayout.success(updatedBoard));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(saveDashboardLayout.failure());
  }
}

function* onSaveDashboardSettings({ payload }: ReturnType<typeof saveDashboardSettings.request>) {
  try {
    const { scope, settings } = payload;

    const boards: Dashboard[] = yield select(getBoards);

    const board = boards.find(
      (board) => board.teamId === scope.teamId && board.userId === scope.userId
    );

    if (!board) {
      yield put(saveDashboardSettings.failure());
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const boardPayload: Dashboard = { ...board, settings: { ...board.settings, ...settings } };
    const updatedBoard: Dashboard = yield callApi("updateDashboard", orgId, boardPayload);
    yield put(saveDashboardSettings.success(updatedBoard));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(saveDashboardSettings.failure());
  }
}

function* onOverrideTeamDashboards({ payload }: ReturnType<typeof overrideTeamDashboards.request>) {
  try {
    const { allTeamsUsers, teamIds } = payload;
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const orgDashboardResponse: ListResponse<Dashboard> = yield callApi("fetchDashboards", orgId, {
      teamId: undefined,
      userId: undefined,
    });

    const teamDashboardsResponse: ListResponse<Dashboard> = yield callApi(
      "fetchDashboards",
      orgId,
      undefined,
      {
        $filters: {
          $and: [{ teamId: allTeamsUsers ? { $ne: null } : { $in: teamIds } }, { userId: null }],
        },
      }
    );

    const noTeamUsersDashboardsResponse: ListResponse<Dashboard> = allTeamsUsers
      ? yield callApi("fetchDashboards", orgId, undefined, {
          $filters: {
            $and: [{ teamId: null }, { userId: { $ne: null } }],
          },
        })
      : { data: [] };

    const dashboardsToBeUpdated = [
      ...teamDashboardsResponse.data,
      ...noTeamUsersDashboardsResponse.data,
    ];

    const currentUserId: User["id"] = yield select(getCurrentUserId);
    const orgDashboard = orgDashboardResponse.data[0];

    if (!orgDashboard) {
      yield put(overrideTeamDashboards.failure());
      return;
    }

    const now = new Date().toISOString();

    const affectedBoards = dashboardsToBeUpdated.map((dashboard) => {
      const updatedCards = orgDashboard.settings.cards.map((card) => ({
        ...card,
        id: v4(),
        updatedAt: now,
        updatedBy: { id: currentUserId },
      }));

      return {
        ...dashboard,
        settings: { ...dashboard.settings, cards: updatedCards, dashboardOverriddenAt: now },
      };
    });

    const updatedBoards: Dashboard[] = yield all(
      affectedBoards.map((board) => callApi("updateDashboard", orgId, board))
    );
    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        description: intl.formatMessage(notificationMessages.description, { allTeamsUsers }),
        message: intl.formatMessage(notificationMessages.title),
      });
    }
    yield put(overrideTeamDashboards.success(updatedBoards));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(overrideTeamDashboards.failure());
  }
}

// A `@chart:<id>` mention pill can be rendered anywhere — the notification panel included — and
// those surfaces must not reach into this scene to route. They dispatch instead, and the scene,
// which owns both the scope and the chart pathname, opens it.
function* onOpenChartMention({ payload: { chartId } }: ReturnType<typeof openChartMention>) {
  const scope: Scope = yield select(getScope);
  yield put(push(getChartPathname(scope, chartId)));
}

const isLoadMoreAction = (action: Action) =>
  isActionOf(fetchTimelineEvents.request, action) && isLoadMorePayload(action.payload);
const isLoadNewAction = (action: Action) =>
  isActionOf(fetchTimelineEvents.request, action) && !isLoadMorePayload(action.payload);

export function* dashboardSaga() {
  yield fork(coachingSagas);
  yield fork(stackRankSagas);
  yield fork(recordsCadenceEffectivenessSagas);
  yield fork(activitiesLoggedSagas);
  yield fork(activitiesOverdueSagas);
  yield fork(averageStopsPerRouteSagas);
  yield fork(checkInsSagas);
  yield fork(closedStageDealAmountSagas);
  yield fork(customerFaceTimeSagas);
  yield fork(closedStageDealCountSagas);
  yield fork(distanceTravelledSagas);
  yield fork(efficiencyTrackerSagas);
  yield fork(fieldTimeSagas);
  yield fork(mileageTrackingSagas);
  yield fork(newRecordsSagas);
  yield fork(newRoutesSagas);
  yield fork(noContactInSagas);
  yield fork(noteCompletionSagas);
  yield fork(outOfFrequencySagas);
  yield fork(openStageDealAmountSagas);
  yield fork(recordsPastDueSagas);
  yield fork(skippedRecordsSagas);
  yield fork(upcomingActivitiesSagas);
  yield fork(executiveSummaryScheduleSagas);
  yield fork(selectionEntitiesSagas);
  yield fork(weeklySummarySagas);
  yield fork(weeklySummarySettingsSagas);
  yield fork(wordCloudSagas);
  yield fork(wordCloudSettingsSagas);
  yield takeLatest(changeScope.request, onChangeScope);
  yield takeLatest(isLoadNewAction, onFetchTimelineEvents);
  yield takeEvery(isLoadMoreAction, onFetchTimelineEvents);
  yield takeEvery(addCard.request, onAddCard);
  yield takeEvery(saveCard.request, onSaveCard);
  yield takeEvery(removeCard.request, onRemoveCard);
  yield takeLatest(saveDashboardLayout.request, onSaveDashboardLayout);
  yield takeLatest(saveDashboardSettings.request, onSaveDashboardSettings);
  yield takeLatest(overrideTeamDashboards.request, onOverrideTeamDashboards);
  yield takeLatest(openChartMention, onOpenChartMention);
}
